import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';

// --- Types ---

/**
 * PROD1.7.6 — Composite state persisted in localStorage when `persistKey` is
 * set on FilterableTable. Stored as a single JSON blob so we can compare the
 * restored state to defaults in one shot for the "restored" badge.
 */
interface PersistedTableState {
  sortKey: string | null;
  sortDir: 'asc' | 'desc' | null;
  filters: Record<string, string[]>;
  searches: Record<string, string>;
  /** PROD1.7.8 follow-up — taille de page persistée (pagination client-side). */
  pageSize?: number;
}

/** Default options exposed in the page-size selector. */
export const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250] as const;
/** Default page size used by FilterableTable when pagination is enabled. */
export const DEFAULT_PAGE_SIZE = 100;

export interface FilterableColumnDef<T> {
  /** Unique key for the column */
  key: string;
  /** Header label */
  header: string;
  /** Render cell content */
  render: (row: T) => React.ReactNode;
  /** Enable auto-filter on this column */
  filterable?: boolean;
  /**
   * Extract filter values from a row. Return a string or array of strings.
   * If omitted, uses String(row[key]).
   */
  filterValueFn?: (row: T) => string | string[];
  /**
   * Provide filter options explicitly instead of auto-extracting from data.
   * Each option has a value (used for matching) and a label (displayed).
   */
  filterOptions?: { value: string; label: string }[];
  /** Enable text search on this column */
  searchable?: boolean;
  /** Enable sorting on this column */
  sortable?: boolean;
  /**
   * Extract a sortable string value from a row.
   * If omitted, uses filterValueFn or String(row[key]).
   */
  sortValueFn?: (row: T) => string;
  /**
   * Multi-criteria sort: define several sortable fields on the same column.
   * Mutually exclusive with `sortable`+`sortValueFn` — if both are set,
   * `sortOptions` wins.
   *
   * Each option exposes its own `sortValueFn`; the dropdown lets the user
   * pick which one to sort on. The column key + option key are concatenated
   * internally to form a unique sort key (`{colKey}::{optionKey}`).
   *
   * Example: a "Dates" column with sortOptions [last_action, upload, extracted, validated].
   */
  sortOptions?: Array<{
    key: string;
    label: string;
    sortValueFn: (row: T) => string | number;
  }>;
  /** Header text alignment */
  align?: 'left' | 'center' | 'right';
  /** Cell className */
  className?: string;
  /** Header className */
  headerClassName?: string;
  /** Column width (e.g. '200px', '15%', 'auto') */
  width?: string;
  /**
   * Multiple INDEPENDENT filter dropdowns rendered in the same column header.
   *
   * Use this when a single visual column aggregates several logical fields
   * (ex: column "Configs" showing Schema/Prompt/LLM/OCR versions in a 2x2
   * cell — each axis keeps its own multi-select filter + URL param).
   *
   * Each sub-filter has its own `key` (used in controlledFilters /
   * onFiltersChange), its own `filterOptions`, and its own `filterValueFn`.
   * The dropdown shows them as labeled sections.
   *
   * Mutually exclusive with `filterable`+`filterOptions` on the same column
   * (if both are set, subFilters wins for filtering; the legacy `filterable`
   * is ignored). Tri (`sortable` / `sortOptions`) reste indépendant.
   */
  subFilters?: Array<{
    key: string;
    label: string;
    filterOptions: { value: string; label: string }[];
    filterValueFn: (row: T) => string | string[];
  }>;
}

export interface FilterableTableProps<T> {
  data: T[];
  columns: FilterableColumnDef<T>[];
  /** Unique key extractor for each row */
  rowKey: (row: T) => string | number;
  /** Optional row className */
  rowClassName?: (row: T) => string;
  /** Message when no data after filtering */
  emptyMessage?: string;
  /** Message when data array is empty (before filtering) */
  noDataMessage?: string;
  /** Default sort on initial render (column key + direction) */
  defaultSort?: { key: string; direction: SortDirection };
  /** Enable row selection with checkboxes */
  selectable?: boolean;
  /** Controlled selection state (set of row keys) */
  selectedKeys?: Set<string | number>;
  /** Callback when selection changes */
  onSelectionChange?: (selectedKeys: Set<string | number>) => void;
  /**
   * When provided, rows for which this returns false cannot be selected:
   * their checkbox is disabled and they are excluded from the header
   * "select all" (which only toggles selectable rows). Defaults to all rows
   * selectable. Used e.g. to prevent picking `skip_benchmark` GT docs.
   */
  isRowSelectable?: (row: T) => boolean;
  /**
   * By default, selections that leave the filtered view are auto-purged (so the
   * header checkbox can fully clear). Set true to KEEP selected keys even when
   * they're filtered out — use when filters/search are only for navigating a
   * selection the user is building (e.g. picking GT docs for a benchmark run),
   * not for scoping bulk actions to the visible rows. "Select all" then still
   * only toggles the visible/filtered rows.
   */
  keepSelectionOnFilter?: boolean;
  /** Callback when filtered data count changes */
  onFilteredCountChange?: (filteredCount: number, totalCount: number) => void;
  /**
   * When provided, sorting is delegated to the parent (server-side sort).
   * The table will NOT sort `data` locally; instead, every sort interaction
   * fires this callback. The header indicator follows `controlledSort`.
   */
  onSortChange?: (key: string | null, direction: SortDirection | null) => void;
  /** Controlled sort state — required when `onSortChange` is provided. */
  controlledSort?: { key: string; direction: SortDirection } | null;
  /**
   * When provided, checkbox filters are delegated to the parent (server-side).
   * The table will NOT filter `data` locally on these columns; clicking a
   * checkbox fires this callback. The dropdown shows `controlledFilters`.
   */
  onFiltersChange?: (key: string, selected: string[]) => void;
  /** Controlled filter state per column — paired with onFiltersChange. */
  controlledFilters?: Record<string, string[]>;
  /**
   * When provided, search inputs are delegated to the parent (server-side).
   * The table will NOT search `data` locally on these columns; typing in the
   * input fires this callback (debounce is the parent's responsibility). The
   * input value shows `controlledSearches`.
   */
  onSearchChange?: (key: string, text: string) => void;
  /** Controlled search state per column — paired with onSearchChange. */
  controlledSearches?: Record<string, string>;
  /**
   * PROD1.7.6 — When set, persist sort + filters + searches in localStorage
   * under the key `filterable-table:{persistKey}`. Only effective for the
   * NON-CONTROLLED dimensions (sort/filters/searches without the matching
   * `onXChange` callback). Use a stable, route-scoped string (e.g.
   * `parametres-schemas`) — renaming the key wipes saved preferences.
   */
  persistKey?: string;
  /**
   * PROD1.7.8 follow-up — pagination client-side optionnelle.
   * Quand `paginated: true`, le tableau découpe `processedData` (post-filtres,
   * post-tri) en pages et affiche un footer avec contrôles. La sélection
   * (checkboxes) couvre toujours TOUT le dataset filtré, pas juste la page
   * visible — c'est le comportement attendu pour les actions bulk.
   *
   * Si `persistKey` est aussi fourni, la `pageSize` choisie est persistée
   * dans localStorage avec les autres préférences du tableau.
   */
  paginated?: boolean;
  /** Taille de page par défaut (défaut : DEFAULT_PAGE_SIZE = 100). */
  defaultPageSize?: number;
  /** Options proposées dans le sélecteur (défaut : DEFAULT_PAGE_SIZE_OPTIONS). */
  pageSizeOptions?: readonly number[];
}

export interface FilterableTableHandle {
  resetFilters: () => void;
  /** Clés (rowKey) des lignes actuellement dans la vue filtrée/triée (tout le
   *  dataset filtré, pas seulement la page visible). Sert au parent pour savoir
   *  quels éléments sélectionnés sont HORS du filtre courant (actions bulk). */
  getFilteredKeys: () => Array<string | number>;
}

type SortDirection = 'asc' | 'desc';

// --- ColumnHeaderDropdown (internal) ---

interface SubFilterProps {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

function ColumnHeaderDropdown({
  label,
  options,
  selected,
  onChange,
  subFilters,
  searchable,
  searchText,
  onSearchChange,
  sortable,
  sortDirection,
  onSort,
  sortOptions,
  activeSortOptionKey,
  onSortOption,
  align = 'left',
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  subFilters?: SubFilterProps[];
  searchable?: boolean;
  searchText: string;
  onSearchChange: (text: string) => void;
  sortable?: boolean;
  sortDirection: SortDirection | null;
  onSort: (dir: SortDirection | null) => void;
  sortOptions?: Array<{ key: string; label: string }>;
  activeSortOptionKey?: string | null;
  onSortOption?: (optionKey: string, dir: SortDirection | null) => void;
  align?: 'left' | 'center' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, handleClickOutside]);

  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [open, searchable]);

  const isFilterActive = selected.length > 0;
  const isSubFilterActive = (subFilters ?? []).some((s) => s.selected.length > 0);
  const isSearchActive = searchText.length > 0;
  const isActive = isFilterActive || isSubFilterActive || isSearchActive || sortDirection !== null;

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const handleSort = (dir: SortDirection) => {
    onSort(sortDirection === dir ? null : dir);
  };

  const alignClass =
    align === 'center' ? 'left-1/2 -translate-x-1/2' :
    align === 'right' ? 'right-0' : 'left-0';

  const sortIcon = sortDirection === 'asc' ? ' \u2191' : sortDirection === 'desc' ? ' \u2193' : '';

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 font-semibold text-sm whitespace-nowrap ${
          isActive ? 'text-blue-700' : 'text-gray-700'
        } hover:text-blue-600`}
      >
        {label}{sortIcon}
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive ? 'bg-blue-600' : 'bg-transparent'}`} />
        <svg
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className={`absolute z-20 mt-1 bg-white border border-gray-200 rounded shadow-lg min-w-[180px] max-h-72 overflow-y-auto ${alignClass}`}>
          {/* Multi-criteria sort options (PROD1.4 \u2014 colonne 'Dates') */}
          {sortOptions && sortOptions.length > 0 && onSortOption && (
            <div className="border-b">
              {sortOptions.map((opt) => {
                const isActive = activeSortOptionKey === opt.key;
                const dirIcon =
                  isActive && sortDirection === 'asc' ? '\u2191' :
                  isActive && sortDirection === 'desc' ? '\u2193' : '';
                // 3 \u00e9tats : inactif \u2192 desc \u2192 asc \u2192 inactif (cycle au clic)
                const nextDir: SortDirection | null =
                  !isActive ? 'desc' :
                  sortDirection === 'desc' ? 'asc' :
                  null;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => onSortOption(opt.key, nextDir)}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${
                      isActive ? 'text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    <span>{opt.label}</span>
                    <span className="text-xs">{dirIcon}</span>
                  </button>
                );
              })}
            </div>
          )}
          {/* Sort options (single criterion) */}
          {sortable && !sortOptions && (
            <>
              <button
                type="button"
                onClick={() => handleSort('asc')}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                  sortDirection === 'asc' ? 'text-blue-700 font-medium' : 'text-gray-700'
                }`}
              >
                <span className="text-xs">{'\u2191'}</span> Trier A-Z
              </button>
              <button
                type="button"
                onClick={() => handleSort('desc')}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 border-b ${
                  sortDirection === 'desc' ? 'text-blue-700 font-medium' : 'text-gray-700'
                }`}
              >
                <span className="text-xs">{'\u2193'}</span> Trier Z-A
              </button>
            </>
          )}
          {/* Search input */}
          {searchable && (
            <div className={`px-2 py-1.5 ${sortable || options.length > 0 ? 'border-b' : ''}`}>
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchText}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full border rounded px-2 py-1 text-sm pr-6"
                />
                {searchText && (
                  <button
                    type="button"
                    onClick={() => onSearchChange('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Filter options */}
          {options.length > 0 && (
            <>
              {isFilterActive && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 border-b"
                >
                  Effacer le filtre
                </button>
              )}
              {options.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="rounded border-gray-300 text-blue-600 flex-shrink-0"
                  />
                  <span className="text-sm whitespace-nowrap">{opt.label}</span>
                </label>
              ))}
            </>
          )}
          {/* Sub-filter sections (multiple independent filters in one column) */}
          {subFilters && subFilters.length > 0 && subFilters.map((sub, idx) => {
            const subSelected = sub.selected;
            const subIsActive = subSelected.length > 0;
            const toggleSub = (value: string) => {
              sub.onChange(
                subSelected.includes(value)
                  ? subSelected.filter((v) => v !== value)
                  : [...subSelected, value]
              );
            };
            return (
              <div key={sub.key} className={idx > 0 ? 'border-t' : ''}>
                <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50">
                  {sub.label}
                </div>
                {subIsActive && (
                  <button
                    type="button"
                    onClick={() => sub.onChange([])}
                    className="w-full text-left px-3 py-1 text-xs text-gray-500 hover:bg-gray-50"
                  >
                    Effacer
                  </button>
                )}
                {sub.options.length === 0 ? (
                  <div className="px-3 py-1.5 text-xs text-gray-400 italic">Aucune valeur</div>
                ) : (
                  sub.options.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={subSelected.includes(opt.value)}
                        onChange={() => toggleSub(opt.value)}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- FilterableTable ---

function FilterableTableInner<T>({
  data,
  columns,
  rowKey,
  rowClassName,
  emptyMessage = 'Aucun résultat avec les filtres actuels',
  noDataMessage = 'Aucune donnée disponible',
  defaultSort,
  selectable,
  selectedKeys,
  onSelectionChange,
  isRowSelectable,
  keepSelectionOnFilter,
  onFilteredCountChange,
  onSortChange,
  controlledSort,
  onFiltersChange,
  controlledFilters,
  onSearchChange,
  controlledSearches,
  persistKey,
  paginated = false,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: FilterableTableProps<T>, ref: React.Ref<FilterableTableHandle>) {
  const isServerFilters = !!onFiltersChange;
  const isServerSearches = !!onSearchChange;
  const isServerSort = !!onSortChange;

  // PROD1.7.6 — Persist non-controlled state (sort + filters + searches) in
  // localStorage when persistKey is provided. The four dimensions are stored
  // together as a single JSON blob so we can detect "any non-default state
  // restored" cheaply for the badge.
  const defaultPersisted: PersistedTableState = {
    sortKey: defaultSort?.key ?? null,
    sortDir: defaultSort?.direction ?? null,
    filters: {},
    searches: {},
  };
  const storageKey = persistKey ? `filterable-table:${persistKey}` : null;
  const [persisted, setPersisted] = useLocalStorage<PersistedTableState>(
    storageKey,
    defaultPersisted,
  );

  const internalFilters = persisted.filters;
  const internalSearches = persisted.searches;
  const internalSortKey = persisted.sortKey;
  const internalSortDir = persisted.sortDir;

  // PROD1.7.8 follow-up — pagination state.
  // pageSize : persisté via le même blob `persisted` (persistKey), pour partager
  //   les préférences utilisateur du tableau en un seul endroit.
  // page    : intentionnellement PAS persisté — il se reset à 1 au reload
  //   et automatiquement quand un filtre change la taille du dataset (cf. useEffect plus bas).
  const persistedPageSize = persisted.pageSize ?? defaultPageSize;
  const [page, setPage] = useState<number>(1);
  const pageSize = paginated ? persistedPageSize : Infinity;

  const filters = isServerFilters ? (controlledFilters ?? {}) : internalFilters;
  const searches = isServerSearches ? (controlledSearches ?? {}) : internalSearches;
  const sortKey = isServerSort ? (controlledSort?.key ?? null) : internalSortKey;
  const sortDir = isServerSort ? (controlledSort?.direction ?? null) : internalSortDir;

  // Build filter options for each filterable column
  const columnOptions = useMemo(() => {
    const result: Record<string, { value: string; label: string }[]> = {};
    for (const col of columns) {
      if (!col.filterable) continue;

      if (col.filterOptions) {
        result[col.key] = col.filterOptions;
      } else {
        // Auto-extract distinct values from data
        const valuesSet = new Set<string>();
        for (const row of data) {
          const val = col.filterValueFn
            ? col.filterValueFn(row)
            : String((row as Record<string, unknown>)[col.key] ?? '');
          if (Array.isArray(val)) {
            val.forEach((v) => v && valuesSet.add(v));
          } else if (val) {
            valuesSet.add(val);
          }
        }
        result[col.key] = Array.from(valuesSet)
          .sort((a, b) => a.localeCompare(b))
          .map((v) => ({ value: v, label: v }));
      }
    }
    return result;
  }, [data, columns]);

  // Get text value for a row (used by sort and search)
  const getTextValue = useCallback((row: T, col: FilterableColumnDef<T>): string => {
    if (col.sortValueFn) return col.sortValueFn(row);
    if (col.filterValueFn) {
      const val = col.filterValueFn(row);
      return Array.isArray(val) ? val.join(', ') : val;
    }
    return String((row as Record<string, unknown>)[col.key] ?? '');
  }, []);

  // Apply checkbox filters, text search, then sort.
  // In server mode (onFiltersChange/onSearchChange), the parent has already
  // filtered the dataset via API — we must not re-filter locally.
  const processedData = useMemo(() => {
    let result = data.filter((row) => {
      if (isServerFilters && isServerSearches) return true;
      // Checkbox filters
      for (const col of columns) {
        if (isServerFilters) continue;
        // subFilters : N filtres logiquement indépendants partageant un header
        if (col.subFilters && col.subFilters.length > 0) {
          for (const sub of col.subFilters) {
            const selected = filters[sub.key];
            if (!selected || selected.length === 0) continue;
            const val = sub.filterValueFn(row);
            if (Array.isArray(val)) {
              if (!val.some((v) => selected.includes(v))) return false;
            } else {
              if (!selected.includes(val)) return false;
            }
          }
          continue;
        }
        if (!col.filterable) continue;
        const selected = filters[col.key];
        if (!selected || selected.length === 0) continue;

        const val = col.filterValueFn
          ? col.filterValueFn(row)
          : String((row as Record<string, unknown>)[col.key] ?? '');

        if (Array.isArray(val)) {
          if (!val.some((v) => selected.includes(v))) return false;
        } else {
          if (!selected.includes(val)) return false;
        }
      }

      // Text search
      for (const col of columns) {
        if (!col.searchable) continue;
        if (isServerSearches) continue;
        const query = searches[col.key];
        if (!query) continue;
        const text = getTextValue(row, col).toLowerCase();
        if (!text.includes(query.toLowerCase())) return false;
      }

      return true;
    });

    // Sort — supports both legacy single-criterion (sortKey == col.key)
    // and multi-criterion (sortKey == "col.key::option.key").
    // Server-side sort: parent already returns data in the desired order,
    // we must not re-sort locally (would only reorder the current page).
    if (!isServerSort && sortKey && sortDir) {
      const [colKey, optKey] = sortKey.split('::');
      const sortCol = columns.find((c) => c.key === colKey);
      if (sortCol) {
        const optFn = optKey
          ? sortCol.sortOptions?.find((o) => o.key === optKey)?.sortValueFn
          : undefined;
        result = [...result].sort((a, b) => {
          if (optFn) {
            const va = optFn(a);
            const vb = optFn(b);
            // numeric or string comparison depending on type
            let cmp: number;
            if (typeof va === 'number' && typeof vb === 'number') {
              cmp = va - vb;
            } else {
              cmp = String(va).localeCompare(String(vb));
            }
            return sortDir === 'asc' ? cmp : -cmp;
          }
          const va = getTextValue(a, sortCol);
          const vb = getTextValue(b, sortCol);
          const cmp = va.localeCompare(vb);
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [data, columns, filters, searches, sortKey, sortDir, getTextValue]);

  // PROD1.7.8 follow-up — pagination calculée à partir du `processedData`
  // (déjà filtré + trié). totalPages min = 1 même si vide pour éviter Page 1/0.
  const totalPages = paginated
    ? Math.max(1, Math.ceil(processedData.length / pageSize))
    : 1;

  // Si la page courante dépasse le nouveau totalPages (ex : un filtre vient
  // de réduire le dataset), on snap automatiquement au max — sinon l'utilisateur
  // verrait une page vide après application d'un filtre.
  useEffect(() => {
    if (paginated && page > totalPages) setPage(totalPages);
  }, [paginated, page, totalPages]);

  const pagedData = useMemo(() => {
    if (!paginated) return processedData;
    const start = (page - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [paginated, processedData, page, pageSize]);

  const handlePageSizeChange = useCallback((next: number) => {
    setPersisted((prev) => ({ ...prev, pageSize: next }));
    setPage(1);
  }, [setPersisted]);

  // Expose resetFilters to parent via ref. In server mode, both flushing
  // the local state AND notifying the parent are best-effort idempotent
  // operations — the parent owns the canonical state.
  const resetFilters = useCallback(() => {
    // PROD1.7.6 — Reset clears filters/searches AND any persisted sort/searches
    // so that the next mount starts from the user-visible default state.
    setPersisted((prev) => ({
      ...prev,
      filters: {},
      searches: {},
    }));
    if (isServerFilters && onFiltersChange) {
      for (const key of Object.keys(controlledFilters ?? {})) {
        if ((controlledFilters?.[key] ?? []).length > 0) onFiltersChange(key, []);
      }
    }
    if (isServerSearches && onSearchChange) {
      for (const key of Object.keys(controlledSearches ?? {})) {
        if ((controlledSearches?.[key] ?? '').length > 0) onSearchChange(key, '');
      }
    }
  }, [setPersisted, isServerFilters, isServerSearches, onFiltersChange, onSearchChange, controlledFilters, controlledSearches]);

  useImperativeHandle(
    ref,
    () => ({
      resetFilters,
      getFilteredKeys: () => processedData.map((row) => rowKey(row)),
    }),
    [resetFilters, processedData, rowKey]
  );

  // Notify parent of filtered count changes
  useEffect(() => {
    onFilteredCountChange?.(processedData.length, data.length);
  }, [processedData.length, data.length, onFilteredCountChange]);

  const updateFilter = (key: string, selected: string[]) => {
    if (isServerFilters) {
      onFiltersChange?.(key, selected);
      return;
    }
    setPersisted((prev) => ({
      ...prev,
      filters: { ...prev.filters, [key]: selected },
    }));
  };

  const updateSearch = (key: string, text: string) => {
    if (isServerSearches) {
      onSearchChange?.(key, text);
      return;
    }
    setPersisted((prev) => ({
      ...prev,
      searches: { ...prev.searches, [key]: text },
    }));
  };

  const handleSort = (key: string, dir: SortDirection | null) => {
    if (isServerSort) {
      onSortChange?.(dir === null ? null : key, dir);
      return;
    }
    setPersisted((prev) => ({
      ...prev,
      sortKey: dir === null ? null : key,
      sortDir: dir,
    }));
  };

  // Multi-criterion sort handler — composite key "{colKey}::{optionKey}"
  const handleSortOption = (
    colKey: string,
    optionKey: string,
    dir: SortDirection | null,
  ) => {
    if (isServerSort) {
      onSortChange?.(dir === null ? null : `${colKey}::${optionKey}`, dir);
      return;
    }
    setPersisted((prev) => ({
      ...prev,
      sortKey: dir === null ? null : `${colKey}::${optionKey}`,
      sortDir: dir,
    }));
  };

  const hasActiveFilters = Object.values(filters).some((f) => f.length > 0) ||
    Object.values(searches).some((s) => s.length > 0);

  // PROD1.7.6 — Show a discrete badge when state restored from localStorage
  // differs from defaults, so the user understands why the table may not be
  // empty / unsorted. Auto-dismisses after 5s or on user interaction.
  const isNonDefaultState = useMemo(() => {
    if (!persistKey) return false;
    if (internalSortKey !== defaultPersisted.sortKey) return true;
    if (internalSortDir !== defaultPersisted.sortDir) return true;
    if (Object.values(internalFilters).some((v) => v.length > 0)) return true;
    if (Object.values(internalSearches).some((v) => v.length > 0)) return true;
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty: snapshot at mount only
  const [showRestoredBadge, setShowRestoredBadge] = useState(isNonDefaultState);
  useEffect(() => {
    if (!showRestoredBadge) return;
    const timer = setTimeout(() => setShowRestoredBadge(false), 5000);
    return () => clearTimeout(timer);
  }, [showRestoredBadge]);

  // Lock column widths after initial render to prevent layout shift when filtering.
  // CRITICAL: these hooks must run on every render (no early return before them),
  // otherwise React throws "Rendered fewer hooks than expected" when `data` goes
  // from N to 0 (e.g. server-side filters returning empty results).
  const tableRef = useRef<HTMLTableElement>(null);
  const lockedWidthsRef = useRef<number[] | null>(null);
  const [, forceUpdate] = useState(0);

  useLayoutEffect(() => {
    if (tableRef.current && !lockedWidthsRef.current && data.length > 0) {
      const ths = tableRef.current.querySelectorAll('thead th');
      lockedWidthsRef.current = Array.from(ths).map((th) => th.getBoundingClientRect().width);
      forceUpdate((n) => n + 1);
    }
  });

  // Check if column has dropdown (filterable, sortable, sortOptions, searchable,
  // or has subFilters — multiple independent filters in one column).
  const hasDropdown = (col: FilterableColumnDef<T>) =>
    (col.filterable && columnOptions[col.key]?.length > 0)
    || col.sortable
    || (col.sortOptions && col.sortOptions.length > 0)
    || col.searchable
    || (col.subFilters && col.subFilters.length > 0);

  // Selection helpers
  const processedKeys = useMemo(
    () => processedData.map((row) => rowKey(row)),
    [processedData, rowKey]
  );

  // Auto-purge des sélections qui ne sont plus dans la vue filtrée.
  // Évite l'état "fantôme" où une row sélectionnée disparaît du tableau
  // après application d'un filtre — la case d'en-tête ne pouvait alors plus
  // décocher complètement (les rows hors-filtre restaient sélectionnées).
  const processedKeysSet = useMemo(() => new Set(processedKeys), [processedKeys]);
  useEffect(() => {
    if (keepSelectionOnFilter) return;
    if (!selectable || !selectedKeys || !onSelectionChange) return;
    if (selectedKeys.size === 0) return;
    let changed = false;
    const next = new Set<string | number>();
    selectedKeys.forEach((k) => {
      if (processedKeysSet.has(k)) {
        next.add(k);
      } else {
        changed = true;
      }
    });
    if (changed) onSelectionChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedKeysSet]);

  // Show the bare empty-placeholder only in legacy client-side mode AND when
  // no filter is active. In server mode the parent owns the dataset — render
  // the full table (header included) so the user can clear / change filters
  // even when the response is empty.
  // CRITICAL: must come AFTER all hooks (useMemo/useEffect above) — otherwise
  // when `data` transitions from [] (mount) to non-empty (after fetch), React
  // throws "Rendered fewer hooks than expected" (StatistiquesPage repro).
  if (
    data.length === 0
    && !isServerFilters
    && !isServerSearches
    && !hasActiveFilters
  ) {
    return <div className="text-gray-500 py-4">{noDataMessage}</div>;
  }

  // Header "select all" and the auto-purge only apply to selectable rows:
  // rows rejected by `isRowSelectable` are never added and don't block the
  // header checkbox from reaching the "all selected" state.
  const selectableProcessedKeys = isRowSelectable
    ? processedData.filter((row) => isRowSelectable(row)).map((row) => rowKey(row))
    : processedKeys;

  const allFilteredSelected = selectable && selectedKeys
    ? selectableProcessedKeys.length > 0 && selectableProcessedKeys.every((k) => selectedKeys.has(k))
    : false;

  const someFilteredSelected = selectable && selectedKeys
    ? selectableProcessedKeys.some((k) => selectedKeys.has(k)) && !allFilteredSelected
    : false;

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allFilteredSelected) {
      // Deselect all selectable filtered rows (keep others selected)
      const next = new Set(selectedKeys);
      selectableProcessedKeys.forEach((k) => next.delete(k));
      onSelectionChange(next);
    } else {
      // Select all selectable filtered rows (add to existing)
      const next = new Set(selectedKeys);
      selectableProcessedKeys.forEach((k) => next.add(k));
      onSelectionChange(next);
    }
  };

  const handleSelectRow = (key: string | number) => {
    if (!onSelectionChange || !selectedKeys) return;
    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onSelectionChange(next);
  };

  const totalColSpan = columns.length + (selectable ? 1 : 0);

  return (
    <div className="relative">
      {/* PROD1.7.6 — Badge éphémère quand le state a été restauré depuis localStorage.
          PROD1.7.8 — Affiché en overlay (position: absolute) centré horizontalement
          et verticalement sur la première ligne (entête) du tableau pour éviter
          tout layout shift quand le badge apparaît/disparaît. La ligne d'entête
          fait ~44px (padding p-3 + texte) ; on positionne le badge au centre via
          top: ~22px et translate-x pour le centrage horizontal. */}
      {showRestoredBadge && (
        <div
          className="absolute z-10 flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-50 text-blue-800 rounded border border-blue-200 shadow-sm"
          style={{ top: '22px', left: '50%', transform: 'translate(-50%, -50%)' }}
          role="status"
        >
          <span>Préférences précédentes restaurées (tri / filtres / recherche).</span>
          <button
            type="button"
            onClick={() => { resetFilters(); setShowRestoredBadge(false); }}
            className="underline hover:text-blue-900"
          >
            Réinitialiser
          </button>
          <button
            type="button"
            onClick={() => setShowRestoredBadge(false)}
            className="ml-auto text-blue-400 hover:text-blue-700 px-1"
            aria-label="Fermer le rappel"
          >
            ×
          </button>
        </div>
      )}
      <table ref={tableRef} className={`w-full border-collapse ${lockedWidthsRef.current ? 'table-fixed' : ''}`}>
        <thead>
          <tr className="bg-primary-50 border-b-2 border-primary-200">
            {selectable && (
              <th className="p-3 text-center w-10" style={lockedWidthsRef.current ? { width: 40 } : undefined}>
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={(el) => { if (el) el.indeterminate = someFilteredSelected; }}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600"
                />
              </th>
            )}
            {columns.map((col, i) => (
              <th
                key={col.key}
                style={lockedWidthsRef.current ? { width: lockedWidthsRef.current[i + (selectable ? 1 : 0)] } : (col.width ? { width: col.width } : undefined)}
                className={`p-3 ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.headerClassName || ''}`}
              >
                {hasDropdown(col) ? (() => {
                  // Compute active sort context for this column.
                  // sortKey may be either "colKey" (legacy) or "colKey::optKey" (multi).
                  const [activeCol, activeOpt] = (sortKey || '').split('::');
                  const colMatches = activeCol === col.key;
                  const activeSortOptionKey = colMatches && activeOpt ? activeOpt : null;
                  // Direction badge in header: only show when this column is active sort.
                  const headerDir = colMatches ? sortDir : null;
                  return (
                    <ColumnHeaderDropdown
                      label={col.header}
                      options={col.filterable ? (columnOptions[col.key] || []) : []}
                      selected={filters[col.key] || []}
                      onChange={(sel) => updateFilter(col.key, sel)}
                      subFilters={col.subFilters?.map((sub) => ({
                        key: sub.key,
                        label: sub.label,
                        options: sub.filterOptions,
                        selected: filters[sub.key] || [],
                        onChange: (sel) => updateFilter(sub.key, sel),
                      }))}
                      searchable={col.searchable}
                      searchText={searches[col.key] || ''}
                      onSearchChange={(text) => updateSearch(col.key, text)}
                      sortable={col.sortable}
                      sortDirection={headerDir}
                      onSort={(dir) => handleSort(col.key, dir)}
                      sortOptions={col.sortOptions}
                      activeSortOptionKey={activeSortOptionKey}
                      onSortOption={(optKey, dir) => handleSortOption(col.key, optKey, dir)}
                      align={col.align}
                    />
                  );
                })() : (
                  <span className="font-semibold text-sm text-gray-700">{col.header}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {processedData.length === 0 ? (
            <tr>
              <td colSpan={totalColSpan} className="p-6 text-center text-gray-500">
                {emptyMessage}
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="ml-2 text-blue-600 hover:underline text-sm"
                  >
                    Effacer tous les filtres
                  </button>
                )}
              </td>
            </tr>
          ) : (
            pagedData.map((row) => {
              const key = rowKey(row);
              const isSelected = selectable && selectedKeys?.has(key);
              const rowSelectable = isRowSelectable ? isRowSelectable(row) : true;
              return (
                <tr key={key} className={`border-b hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : 'bg-white'} ${rowClassName?.(row) || ''}`}>
                  {selectable && (
                    <td className="p-3 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected || false}
                        disabled={!rowSelectable}
                        onChange={() => handleSelectRow(key)}
                        className="rounded border-gray-300 text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={`p-3 ${col.className || ''}`}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {paginated && processedData.length > 0 && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm text-gray-600 border-t border-gray-200 bg-gray-50">
          <div>
            Page <strong>{page}</strong> / {totalPages} ·{' '}
            {processedData.length} {processedData.length > 1 ? 'lignes filtrées' : 'ligne filtrée'}
            {processedData.length !== data.length && (
              <span className="text-gray-400"> sur {data.length}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-2 py-1 rounded border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ‹ Précédent
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-2 py-1 rounded border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Suivant ›
            </button>
            <label className="ml-2 text-xs text-gray-500">
              Par page :{' '}
              <select
                value={persistedPageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                className="ml-1 border border-gray-300 rounded px-1 py-0.5 text-xs"
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

// forwardRef wrapper that preserves generic type
export const FilterableTable = forwardRef(FilterableTableInner) as <T>(
  props: FilterableTableProps<T> & { ref?: React.Ref<FilterableTableHandle> }
) => React.ReactElement;
